import { useCallback } from 'react';

import BottomSheet from '@/components/BottomSheet';
import { useTranslation } from '@/utils/i18n';
import { toast } from '@/stores/toast';
import { useGroupNotifPrefs, useUpdateGroupNotifPrefs } from '@/hooks/queries/groups';
import type { GroupNotifPrefs } from '@/api/groups';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  groupId: number;
  onClose: () => void;
}

/**
 * A6 — per-group notification preferences: mute a busy circle's pushes
 * without touching the global settings or other circles. Opt-out model
 * (default ON); suppresses push only — the in-app badge still shows.
 */
export default function GroupNotifPrefsSheet({ open, groupId, onClose }: Props) {
  const { t } = useTranslation();
  const { data: prefs } = useGroupNotifPrefs(groupId, open);
  const mutation = useUpdateGroupNotifPrefs(groupId);

  const toggle = useCallback(
    (key: keyof GroupNotifPrefs, value: boolean) => {
      mutation.mutate(
        { [key]: value },
        { onError: () => toast.error(t('groups.notifications.updateError')) },
      );
    },
    [mutation, t],
  );

  const rows: { key: keyof GroupNotifPrefs; testid: string; label: string; hint: string }[] = [
    {
      key: 'notif_new_sound',
      testid: 'switch-group-notif-new-sound',
      label: t('groups.notifications.newSound'),
      hint: t('groups.notifications.newSoundHint'),
    },
    {
      key: 'notif_member_events',
      testid: 'switch-group-notif-member-events',
      label: t('groups.notifications.memberEvents'),
      hint: t('groups.notifications.memberEventsHint'),
    },
    {
      key: 'notif_replies',
      testid: 'switch-group-notif-replies',
      label: t('groups.notifications.replies'),
      hint: t('groups.notifications.repliesHint'),
    },
  ];

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('groups.notifications.title')}
      testid="sheet-group-notif-prefs"
    >
      <div className="tw:flex tw:flex-col tw:gap-2">
        {rows.map(({ key, testid, label, hint }) => {
          const checked = prefs?.[key] ?? true;
          return (
            <div
              key={key}
              className="tw:flex tw:min-h-16 tw:shrink-0 tw:items-center tw:gap-3 tw:rounded-2xl tw:bg-dark-primary tw:px-4 tw:py-3"
            >
              <div className="tw:flex tw:flex-1 tw:flex-col">
                <span className="tw:text-base tw:text-white">{label}</span>
                <span className="tw:legals tw:text-grey-4">{hint}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label}
                onClick={() => toggle(key, !checked)}
                data-testid={testid}
                className={cn(
                  'tw:relative tw:h-7 tw:w-12 tw:shrink-0 tw:rounded-full tw:transition-colors',
                  checked ? 'tw:bg-yellow-primary' : 'tw:bg-grey-4/40',
                )}
              >
                <span
                  className={cn(
                    'tw:absolute tw:top-1 tw:size-5 tw:rounded-full tw:bg-white tw:transition-all',
                    checked ? 'tw:left-6' : 'tw:left-1',
                  )}
                />
              </button>
            </div>
          );
        })}
      </div>
    </BottomSheet>
  );
}
